import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

const NODE_WIDTH = 220;
const NODE_HEIGHT = 118;
const COMPACT_NODE_HEIGHT = 34;
const CANVAS_WIDTH = 1040;
const CANVAS_HEIGHT = 560;
const WORKSPACE_WIDTH = 1800;
const WORKSPACE_HEIGHT = 1100;
const WORKSPACE_PADDING = 24;
const MIN_ZOOM = 0.6;
const MAX_ZOOM = 1.8;
const ZOOM_STEP = 0.1;

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function normalizeNumber(value, fallback = 0) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

function clampZoom(value) {
  return clamp(Math.round(normalizeNumber(value, 1) * 100) / 100, MIN_ZOOM, MAX_ZOOM);
}

function getNodeCenter(node) {
  const x = normalizeNumber(node?.x, normalizeNumber(node?.positionX));
  const y = normalizeNumber(node?.y, normalizeNumber(node?.positionY));
  const width = normalizeNumber(node?.width, NODE_WIDTH);
  const height = normalizeNumber(node?.height, NODE_HEIGHT);
  return {
    x: x + (width / 2),
    y: y + (height / 2),
  };
}

function getRectEdgePoint(node, targetPoint) {
  const center = getNodeCenter(node);
  const halfWidth = normalizeNumber(node?.width, NODE_WIDTH) / 2;
  const halfHeight = normalizeNumber(node?.height, NODE_HEIGHT) / 2;
  const deltaX = normalizeNumber(targetPoint?.x) - center.x;
  const deltaY = normalizeNumber(targetPoint?.y) - center.y;

  if (Math.abs(deltaX) < 0.001 && Math.abs(deltaY) < 0.001) {
    return center;
  }

  const scaleX = Math.abs(deltaX) > 0.001 ? halfWidth / Math.abs(deltaX) : Number.POSITIVE_INFINITY;
  const scaleY = Math.abs(deltaY) > 0.001 ? halfHeight / Math.abs(deltaY) : Number.POSITIVE_INFINITY;
  const scale = Math.min(scaleX, scaleY);

  return {
    x: center.x + (deltaX * scale),
    y: center.y + (deltaY * scale),
  };
}

function getStraightEdgeConnection(sourceNode, targetNode) {
  const sourceCenter = getNodeCenter(sourceNode);
  const targetCenter = getNodeCenter(targetNode);
  return {
    source: getRectEdgePoint(sourceNode, targetCenter),
    target: getRectEdgePoint(targetNode, sourceCenter),
  };
}

function getDefaultNodeHeight(mappingCount) {
  return mappingCount > 0 ? NODE_HEIGHT : COMPACT_NODE_HEIGHT;
}

function getViewportBounds(zoom, viewportWidth, viewportHeight) {
  return {
    maxOffsetX: Math.max(0, WORKSPACE_WIDTH - (viewportWidth / zoom)),
    maxOffsetY: Math.max(0, WORKSPACE_HEIGHT - (viewportHeight / zoom)),
  };
}

function clampViewport(offsetX, offsetY, zoom, viewportWidth, viewportHeight) {
  const safeZoom = clampZoom(zoom);
  const bounds = getViewportBounds(safeZoom, viewportWidth, viewportHeight);

  return {
    zoom: safeZoom,
    offsetX: clamp(normalizeNumber(offsetX), 0, bounds.maxOffsetX),
    offsetY: clamp(normalizeNumber(offsetY), 0, bounds.maxOffsetY),
  };
}

export default function KeywordClusterEditor({
  canManage,
  tree,
  onDragStateChange,
  onCreateTopic,
  onUpdateTopic,
  onDeleteTopic,
  onCreateEdge,
  onDeleteEdge,
  onAddMappings,
  onRemoveMappings,
}) {
  const [newTopicName, setNewTopicName] = useState('');
  const [selectedTopicId, setSelectedTopicId] = useState('');
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [connectSourceId, setConnectSourceId] = useState('');
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuNode, setMenuNode] = useState(null);
  const [expandedNodeKeywords, setExpandedNodeKeywords] = useState({});

  const [dragging, setDragging] = useState(null);
  const [isPanning, setIsPanning] = useState(false);
  const draggingRef = useRef(null);
  const draggingStateRef = useRef(null);
  const panningRef = useRef(null);
  const suppressClickUntilRef = useRef(0);
  const canvasRef = useRef(null);
  const [nodeSizes, setNodeSizes] = useState({});
  const [canvasSize, setCanvasSize] = useState({ width: CANVAS_WIDTH, height: CANVAS_HEIGHT });
  const [viewport, setViewport] = useState({ zoom: 1, offsetX: 0, offsetY: 0 });
  const viewportRef = useRef({ zoom: 1, offsetX: 0, offsetY: 0 });
  const hasInitializedViewportRef = useRef(false);

  const nodes = useMemo(() => (Array.isArray(tree?.nodes) ? tree.nodes : []), [tree?.nodes]);
  const edges = useMemo(() => (Array.isArray(tree?.edges) ? tree.edges : []), [tree?.edges]);
  const mappings = useMemo(() => (Array.isArray(tree?.mappings) ? tree.mappings : []), [tree?.mappings]);
  const rawKeywords = useMemo(() => (Array.isArray(tree?.rawKeywords) ? tree.rawKeywords : []), [tree?.rawKeywords]);

  const nodeById = useMemo(() => {
    const map = new Map();
    nodes.forEach((node) => map.set(Number(node.id), node));
    return map;
  }, [nodes]);

  const mappingsByNode = useMemo(() => {
    const map = new Map();
    mappings.forEach((mapping) => {
      const nodeId = Number(mapping.nodeId);
      if (!map.has(nodeId)) {
        map.set(nodeId, []);
      }
      map.get(nodeId).push(mapping);
    });
    return map;
  }, [mappings]);

  const edgeLabelById = useMemo(() => {
    const map = new Map();
    edges.forEach((edge) => {
      const sourceLabel = nodeById.get(Number(edge.sourceNodeId))?.name || '-';
      const targetLabel = nodeById.get(Number(edge.targetNodeId))?.name || '-';
      map.set(Number(edge.id), `${sourceLabel} -> ${targetLabel}`);
    });
    return map;
  }, [edges, nodeById]);

  const connectedEdgeIdsForMenuNode = useMemo(() => {
    const nodeId = Number(menuNode?.id);
    if (!Number.isInteger(nodeId) || nodeId <= 0) {
      return [];
    }

    return edges
      .filter((edge) => Number(edge.sourceNodeId) === nodeId || Number(edge.targetNodeId) === nodeId)
      .map((edge) => Number(edge.id))
      .filter((edgeId) => Number.isInteger(edgeId) && edgeId > 0);
  }, [edges, menuNode?.id]);

  useEffect(() => {
    viewportRef.current = viewport;
  }, [viewport]);

  const updateViewport = useCallback((nextValue) => {
    setViewport((previous) => {
      const rawNext = typeof nextValue === 'function' ? nextValue(previous) : nextValue;
      return clampViewport(
        rawNext?.offsetX ?? previous.offsetX,
        rawNext?.offsetY ?? previous.offsetY,
        rawNext?.zoom ?? previous.zoom,
        Math.max(1, normalizeNumber(canvasSize?.width, CANVAS_WIDTH)),
        Math.max(1, normalizeNumber(canvasSize?.height, CANVAS_HEIGHT)),
      );
    });
  }, [canvasSize?.height, canvasSize?.width]);

  useLayoutEffect(() => {
    const canvasElement = canvasRef.current;
    if (!canvasElement) {
      return;
    }

    const measureCanvas = () => {
      const nextWidth = Math.round(canvasElement.clientWidth || CANVAS_WIDTH);
      const nextHeight = Math.round(canvasElement.clientHeight || CANVAS_HEIGHT);
      setCanvasSize((previous) => (
        previous.width === nextWidth && previous.height === nextHeight
          ? previous
          : { width: nextWidth, height: nextHeight }
      ));
    };

    measureCanvas();

    const nextNodeSizes = {};
    canvasElement.querySelectorAll('[data-cluster-node-id]').forEach((element) => {
      const nodeId = Number(element.getAttribute('data-cluster-node-id'));
      if (!Number.isInteger(nodeId) || nodeId <= 0) {
        return;
      }

      nextNodeSizes[nodeId] = {
        width: Math.round(element.offsetWidth || NODE_WIDTH),
        height: Math.round(element.offsetHeight || COMPACT_NODE_HEIGHT),
      };
    });

    setNodeSizes((previous) => {
      const previousKeys = Object.keys(previous);
      const nextKeys = Object.keys(nextNodeSizes);
      const hasSameShape = previousKeys.length === nextKeys.length
        && nextKeys.every((key) => previous[key]?.width === nextNodeSizes[key]?.width && previous[key]?.height === nextNodeSizes[key]?.height);

      return hasSameShape ? previous : nextNodeSizes;
    });

    if (typeof ResizeObserver !== 'function') {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(() => {
      measureCanvas();
    });
    resizeObserver.observe(canvasElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [edges, expandedNodeKeywords, mappingsByNode, nodes, viewport.zoom, viewport.offsetX, viewport.offsetY]);

  const nodeLayoutById = useMemo(() => {
    const map = new Map();
    nodes.forEach((node) => {
      const nodeId = Number(node.id);
      const dragNode = dragging && Number(dragging.nodeId) === nodeId
        ? dragging
        : node;
      const measuredSize = nodeSizes[nodeId];
      const mappingCount = (mappingsByNode.get(nodeId) || []).length;
      const width = Math.max(NODE_WIDTH, normalizeNumber(measuredSize?.width, NODE_WIDTH));
      const height = Math.max(getDefaultNodeHeight(mappingCount), normalizeNumber(measuredSize?.height, getDefaultNodeHeight(mappingCount)));
      const x = clamp(
        normalizeNumber(dragNode?.x, normalizeNumber(dragNode?.positionX, WORKSPACE_PADDING)),
        WORKSPACE_PADDING,
        WORKSPACE_WIDTH - width - WORKSPACE_PADDING,
      );
      const y = clamp(
        normalizeNumber(dragNode?.y, normalizeNumber(dragNode?.positionY, WORKSPACE_PADDING)),
        WORKSPACE_PADDING,
        WORKSPACE_HEIGHT - height - WORKSPACE_PADDING,
      );

      map.set(nodeId, {
        id: nodeId,
        x,
        y,
        width,
        height,
      });
    });

    return map;
  }, [dragging, mappingsByNode, nodeSizes, nodes]);

  const zoomAtPoint = useCallback((nextZoom, clientX, clientY) => {
    const canvasElement = canvasRef.current;
    const safeZoom = clampZoom(nextZoom);

    if (!canvasElement) {
      updateViewport((previous) => ({ ...previous, zoom: safeZoom }));
      return;
    }

    const rect = canvasElement.getBoundingClientRect();
    const localX = clamp(clientX - rect.left, 0, rect.width || canvasSize.width || CANVAS_WIDTH);
    const localY = clamp(clientY - rect.top, 0, rect.height || canvasSize.height || CANVAS_HEIGHT);

    updateViewport((previous) => {
      const worldX = previous.offsetX + (localX / previous.zoom);
      const worldY = previous.offsetY + (localY / previous.zoom);
      return {
        zoom: safeZoom,
        offsetX: worldX - (localX / safeZoom),
        offsetY: worldY - (localY / safeZoom),
      };
    });
  }, [canvasSize.height, canvasSize.width, updateViewport]);

  const handleFitView = useCallback(() => {
    const layouts = Array.from(nodeLayoutById.values());
    if (!layouts.length) {
      updateViewport({ zoom: 1, offsetX: 0, offsetY: 0 });
      return;
    }

    const minX = Math.max(0, Math.min(...layouts.map((item) => item.x)) - 40);
    const minY = Math.max(0, Math.min(...layouts.map((item) => item.y)) - 40);
    const maxX = Math.min(WORKSPACE_WIDTH, Math.max(...layouts.map((item) => item.x + item.width)) + 40);
    const maxY = Math.min(WORKSPACE_HEIGHT, Math.max(...layouts.map((item) => item.y + item.height)) + 40);
    const contentWidth = Math.max(1, maxX - minX);
    const contentHeight = Math.max(1, maxY - minY);
    const viewportWidth = Math.max(1, normalizeNumber(canvasSize.width, CANVAS_WIDTH));
    const viewportHeight = Math.max(1, normalizeNumber(canvasSize.height, CANVAS_HEIGHT));
    const fittedZoom = clampZoom(Math.min(viewportWidth / contentWidth, viewportHeight / contentHeight));

    updateViewport({
      zoom: fittedZoom,
      offsetX: minX - ((viewportWidth / fittedZoom) - contentWidth) / 2,
      offsetY: minY - ((viewportHeight / fittedZoom) - contentHeight) / 2,
    });
  }, [canvasSize.height, canvasSize.width, nodeLayoutById, updateViewport]);

  useEffect(() => {
    if (!nodes.length) {
      hasInitializedViewportRef.current = false;
      updateViewport({ zoom: 1, offsetX: 0, offsetY: 0 });
      return;
    }

    if (!hasInitializedViewportRef.current) {
      hasInitializedViewportRef.current = true;
      handleFitView();
    }
  }, [handleFitView, nodes.length, tree?.year, updateViewport]);

  const startDragging = (pointerId, nodeId, x, y, nodeWidth, nodeHeight, clientX, clientY) => {
    if (!canManage) {
      return;
    }

    if (connectSourceId) {
      return;
    }

    draggingRef.current = {
      pointerId,
      nodeId,
      startClientX: clientX,
      startClientY: clientY,
      startX: x,
      startY: y,
      nodeWidth,
      nodeHeight,
      moved: false,
    };
    draggingStateRef.current = { nodeId, x, y };
    onDragStateChange?.(true);
    setDragging({ nodeId, x, y });
  };

  const startPanning = (pointerId, clientX, clientY) => {
    if (draggingRef.current) {
      return;
    }

    panningRef.current = {
      pointerId,
      startClientX: clientX,
      startClientY: clientY,
      startOffsetX: viewportRef.current.offsetX,
      startOffsetY: viewportRef.current.offsetY,
    };
    setIsPanning(true);
    onDragStateChange?.(true);
  };

  const finishPointerInteraction = useCallback(async (pointerId) => {
    const isDragPointer = draggingRef.current?.pointerId === pointerId;
    const isPanPointer = panningRef.current?.pointerId === pointerId;

    if (!isDragPointer && !isPanPointer) {
      return;
    }

    let hadInteraction = false;

    if (isDragPointer && draggingRef.current) {
      hadInteraction = true;
      const current = draggingRef.current;
      const finalState = draggingStateRef.current;
      draggingRef.current = null;

      if (finalState) {
        const moved = Math.abs(finalState.x - current.startX) > 1 || Math.abs(finalState.y - current.startY) > 1;
        if (moved) {
          suppressClickUntilRef.current = Date.now() + 250;

          try {
            await onUpdateTopic(current.nodeId, {
              positionX: Math.round(finalState.x),
              positionY: Math.round(finalState.y),
            });
          } finally {
            draggingStateRef.current = null;
            setDragging(null);
          }
        } else {
          draggingStateRef.current = null;
          setDragging(null);
        }
      } else {
        draggingStateRef.current = null;
        setDragging(null);
      }
    }

    if (isPanPointer && panningRef.current) {
      hadInteraction = true;
      panningRef.current = null;
      setIsPanning(false);
    }

    if (hadInteraction) {
      onDragStateChange?.(false);
    }
  }, [onDragStateChange, onUpdateTopic]);

  const handleCanvasPointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }

    const menuTrigger = event.target?.closest?.('[data-cluster-node-menu="true"]');
    if (menuTrigger) {
      return;
    }

    const headerHandle = event.target?.closest?.('[data-cluster-node-handle="true"]');
    if (headerHandle) {
      const nodeId = Number(headerHandle.getAttribute('data-node-id'));
      const layout = nodeLayoutById.get(nodeId);
      if (!layout || !canManage || connectSourceId) {
        return;
      }

      event.preventDefault();
      canvasRef.current?.setPointerCapture?.(event.pointerId);
      startDragging(event.pointerId, nodeId, layout.x, layout.y, layout.width, layout.height, event.clientX, event.clientY);
      return;
    }

    if (event.target?.closest?.('[data-cluster-node-id]')) {
      return;
    }

    event.preventDefault();
    canvasRef.current?.setPointerCapture?.(event.pointerId);
    startPanning(event.pointerId, event.clientX, event.clientY);
  };

  const handleCanvasPointerMove = (event) => {
    if (draggingRef.current?.pointerId === event.pointerId) {
      event.preventDefault();
      const deltaX = (event.clientX - draggingRef.current.startClientX) / viewportRef.current.zoom;
      const deltaY = (event.clientY - draggingRef.current.startClientY) / viewportRef.current.zoom;
      const nodeWidth = normalizeNumber(draggingRef.current.nodeWidth, NODE_WIDTH);
      const nodeHeight = normalizeNumber(draggingRef.current.nodeHeight, NODE_HEIGHT);
      const nextState = {
        nodeId: draggingRef.current.nodeId,
        x: clamp(draggingRef.current.startX + deltaX, WORKSPACE_PADDING, WORKSPACE_WIDTH - nodeWidth - WORKSPACE_PADDING),
        y: clamp(draggingRef.current.startY + deltaY, WORKSPACE_PADDING, WORKSPACE_HEIGHT - nodeHeight - WORKSPACE_PADDING),
      };
      draggingStateRef.current = nextState;
      setDragging(nextState);
      return;
    }

    if (panningRef.current?.pointerId === event.pointerId) {
      event.preventDefault();
      const deltaX = (event.clientX - panningRef.current.startClientX) / viewportRef.current.zoom;
      const deltaY = (event.clientY - panningRef.current.startClientY) / viewportRef.current.zoom;
      updateViewport({
        ...viewportRef.current,
        offsetX: panningRef.current.startOffsetX - deltaX,
        offsetY: panningRef.current.startOffsetY - deltaY,
      });
    }
  };

  const handleCanvasPointerUp = async (event) => {
    event.preventDefault();
    await finishPointerInteraction(event.pointerId);
    canvasRef.current?.releasePointerCapture?.(event.pointerId);
  };

  const handleCanvasPointerCancel = async (event) => {
    await finishPointerInteraction(event.pointerId);
    canvasRef.current?.releasePointerCapture?.(event.pointerId);
  };

  const handleViewportWheel = (event) => {
    if (!event.ctrlKey) {
      return;
    }

    event.preventDefault();
    zoomAtPoint(viewport.zoom + (event.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP), event.clientX, event.clientY);
  };

  const handleZoomStep = (direction) => {
    const canvasElement = canvasRef.current;
    if (!canvasElement) {
      updateViewport({ ...viewport, zoom: viewport.zoom + (direction * ZOOM_STEP) });
      return;
    }

    const rect = canvasElement.getBoundingClientRect();
    zoomAtPoint(viewport.zoom + (direction * ZOOM_STEP), rect.left + (rect.width / 2), rect.top + (rect.height / 2));
  };

  const handleResetView = () => {
    updateViewport({ zoom: 1, offsetX: 0, offsetY: 0 });
  };

  useEffect(() => {
    return () => {
      draggingRef.current = null;
      draggingStateRef.current = null;
      panningRef.current = null;
      setDragging(null);
      setIsPanning(false);
      onDragStateChange?.(false);
    };
  }, [onDragStateChange]);

  const handleToggleKeyword = (normalizedKeyword) => {
    const key = String(normalizedKeyword || '').trim();
    if (!key) {
      return;
    }

    setSelectedKeywords((previous) => {
      if (previous.includes(key)) {
        return previous.filter((value) => value !== key);
      }
      return [...previous, key];
    });
  };

  const handleCreateTopic = async () => {
    const name = String(newTopicName || '').trim();
    if (!name) {
      return;
    }

    await onCreateTopic({
      name,
      color: '#1d4ed8',
      positionX: 24,
      positionY: 24,
    });
    setNewTopicName('');
  };

  const handleAssignSelectedKeywords = async () => {
    const topicId = Number(selectedTopicId);
    if (!Number.isInteger(topicId) || topicId <= 0 || !selectedKeywords.length) {
      return;
    }

    await onAddMappings(topicId, selectedKeywords);
    setSelectedKeywords([]);
  };

  const handleRemoveSelectedKeywords = async () => {
    const topicId = Number(selectedTopicId);
    if (!Number.isInteger(topicId) || topicId <= 0 || !selectedKeywords.length) {
      return;
    }

    await onRemoveMappings(topicId, selectedKeywords);
    setSelectedKeywords([]);
  };

  const handleMenuOpen = (event, node) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuNode(node);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuNode(null);
  };

  const handleStartConnection = (nodeId) => {
    setConnectSourceId(String(nodeId));
    handleMenuClose();
  };

  const handleConnectWithMenuNode = async () => {
    const sourceNodeId = Number(connectSourceId);
    const targetNodeId = Number(menuNode?.id);
    if (!Number.isInteger(sourceNodeId) || !Number.isInteger(targetNodeId) || sourceNodeId <= 0 || targetNodeId <= 0) {
      setConnectSourceId('');
      handleMenuClose();
      return;
    }

    if (sourceNodeId === targetNodeId) {
      handleMenuClose();
      return;
    }

    setConnectSourceId('');
    handleMenuClose();
    await onCreateEdge(sourceNodeId, targetNodeId);
  };

  const handleDisconnectEdge = async (edgeId) => {
    const normalizedEdgeId = Number(edgeId);
    if (!Number.isInteger(normalizedEdgeId) || normalizedEdgeId <= 0) {
      return;
    }

    await onDeleteEdge(normalizedEdgeId);
    handleMenuClose();
  };

  const handleNodeClick = async (nodeId) => {
    if (!canManage) {
      return;
    }

    if (Date.now() < suppressClickUntilRef.current) {
      return;
    }

    if (!connectSourceId) {
      return;
    }

    const sourceNodeId = Number(connectSourceId);
    const targetNodeId = Number(nodeId);
    if (!Number.isInteger(sourceNodeId) || !Number.isInteger(targetNodeId) || sourceNodeId <= 0 || targetNodeId <= 0) {
      setConnectSourceId('');
      return;
    }

    if (sourceNodeId === targetNodeId) {
      setConnectSourceId('');
      return;
    }

    setConnectSourceId('');
    await onCreateEdge(sourceNodeId, targetNodeId);
  };

  const handleRenameNode = async () => {
    if (!menuNode) {
      return;
    }

    const nextName = window.prompt('Neuer Titel der Kategorie', String(menuNode.name || ''));
    if (nextName === null) {
      return;
    }

    const trimmed = String(nextName || '').trim();
    if (!trimmed) {
      return;
    }

    await onUpdateTopic(Number(menuNode.id), { name: trimmed });
    handleMenuClose();
  };

  const handleChangeColor = async (color) => {
    if (!menuNode) {
      return;
    }

    await onUpdateTopic(Number(menuNode.id), { color });
    handleMenuClose();
  };

  const handleDeleteNode = async () => {
    if (!menuNode) {
      return;
    }

    const confirmed = window.confirm(`Kategorie "${menuNode.name}" wirklich löschen?`);
    if (!confirmed) {
      return;
    }

    await onDeleteTopic(Number(menuNode.id));
    handleMenuClose();
  };

  const colorOptions = [
    { value: '#1d4ed8', label: 'Blau' },
    { value: '#16a34a', label: 'Grün' },
    { value: '#ea580c', label: 'Orange' },
    { value: '#be123c', label: 'Rot' },
    { value: '#7e22ce', label: 'Violett' },
  ];

  const workspaceTransform = `translate(${-viewport.offsetX * viewport.zoom}px, ${-viewport.offsetY * viewport.zoom}px) scale(${viewport.zoom})`;
  const workspaceHint = canManage
    ? 'Leere Fläche ziehen = Ansicht verschieben. Kategorien an der Kopfzeile ziehen.'
    : 'Leere Fläche ziehen = Ansicht verschieben.';

  return (
    <Stack spacing={1.25}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
        <Card variant="outlined" sx={{ borderRadius: 2.5, flex: { xs: '1 1 auto', md: '0 0 320px' } }}>
          <CardContent sx={{ p: 1.5 }}>
            <Stack spacing={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Rohbegriffe</Typography>

              <Stack direction="row" spacing={0.75}>
                <TextField
                  size="small"
                  fullWidth
                  label="Neue Kategorie"
                  value={newTopicName}
                  onChange={(event) => setNewTopicName(event.target.value)}
                  disabled={!canManage}
                />
                <Button size="small" variant="outlined" onClick={handleCreateTopic} disabled={!canManage || !newTopicName.trim()}>
                  Erstellen
                </Button>
              </Stack>

              <FormControl size="small" fullWidth>
                <InputLabel id="cluster-topic-select-label">Kategorie</InputLabel>
                <Select
                  labelId="cluster-topic-select-label"
                  label="Kategorie"
                  value={selectedTopicId}
                  onChange={(event) => setSelectedTopicId(event.target.value)}
                >
                  <MenuItem value=""><em>Bitte wählen</em></MenuItem>
                  {nodes.map((node) => (
                    <MenuItem key={`topic-opt-${node.id}`} value={String(node.id)}>{node.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Stack direction="row" spacing={0.75}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleAssignSelectedKeywords}
                  disabled={!canManage || !selectedTopicId || !selectedKeywords.length}
                >
                  Zuordnen
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleRemoveSelectedKeywords}
                  disabled={!canManage || !selectedTopicId || !selectedKeywords.length}
                >
                  Entfernen
                </Button>
              </Stack>

              <List dense disablePadding sx={{ maxHeight: 340, overflow: 'auto', borderRadius: 1.5, border: 1, borderColor: 'divider' }}>
                {rawKeywords.map((keyword) => {
                  const key = String(keyword.key || '');
                  const checked = selectedKeywords.includes(key);
                  return (
                    <ListItem
                      key={`raw-keyword-${key}`}
                      disableGutters
                      secondaryAction={<Chip size="small" label={keyword.count || 0} sx={{ height: 20 }} />}
                      sx={{ px: 0.75, py: 0.1 }}
                    >
                      <Checkbox
                        size="small"
                        checked={checked}
                        onChange={() => handleToggleKeyword(key)}
                      />
                      <ListItemText
                        primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                        primary={keyword.label || key}
                        secondary={keyword.isAssigned ? 'zugeordnet' : 'nicht zugeordnet'}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ borderRadius: 2.5, flex: 1 }}>
          <CardContent sx={{ p: 1.5 }}>
            <Stack spacing={1}>
              <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }} spacing={1}>
                <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Interaktiver Kategorienbaum</Typography>
                    {!!connectSourceId && (
                      <Chip
                        size="small"
                        color="info"
                        label={`Verbindung starten von: ${nodeById.get(Number(connectSourceId))?.name || '-'}`}
                        onDelete={() => setConnectSourceId('')}
                      />
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {workspaceHint}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
                  <Button size="small" variant="outlined" onClick={() => handleZoomStep(-1)} disabled={viewport.zoom <= MIN_ZOOM}>
                    -
                  </Button>
                  <Chip size="small" variant="outlined" label={`${Math.round(viewport.zoom * 100)}%`} />
                  <Button size="small" variant="outlined" onClick={() => handleZoomStep(1)} disabled={viewport.zoom >= MAX_ZOOM}>
                    +
                  </Button>
                  <Button size="small" variant="outlined" onClick={handleFitView}>
                    Einpassen
                  </Button>
                  <Button size="small" variant="text" onClick={handleResetView}>
                    Zurücksetzen
                  </Button>
                </Stack>
              </Stack>

              <Box
                ref={canvasRef}
                onPointerDown={handleCanvasPointerDown}
                onPointerMove={handleCanvasPointerMove}
                onPointerUp={handleCanvasPointerUp}
                onPointerCancel={handleCanvasPointerCancel}
                onWheel={handleViewportWheel}
                sx={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: CANVAS_WIDTH,
                  height: { xs: 420, sm: 500, md: CANVAS_HEIGHT },
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: '#f8fafc',
                  overflow: 'hidden',
                  touchAction: 'none',
                  userSelect: 'none',
                  cursor: isPanning ? 'grabbing' : (connectSourceId ? 'crosshair' : 'grab'),
                }}
              >
                <Box
                  data-cluster-workspace-surface="true"
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: WORKSPACE_WIDTH,
                    height: WORKSPACE_HEIGHT,
                    transform: workspaceTransform,
                    transformOrigin: 'top left',
                  }}
                >
                  <svg
                    width={WORKSPACE_WIDTH}
                    height={WORKSPACE_HEIGHT}
                    style={{ pointerEvents: 'none', position: 'absolute', inset: 0 }}
                  >
                    {edges.map((edge) => {
                      const sourceNode = nodeLayoutById.get(Number(edge.sourceNodeId));
                      const targetNode = nodeLayoutById.get(Number(edge.targetNodeId));
                      if (!sourceNode || !targetNode) {
                        return null;
                      }

                      const connection = getStraightEdgeConnection(sourceNode, targetNode);
                      return (
                        <g key={`edge-${edge.id}`}>
                          <line
                            x1={connection.source.x}
                            y1={connection.source.y}
                            x2={connection.target.x}
                            y2={connection.target.y}
                            stroke="#64748b"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                          <circle cx={connection.source.x} cy={connection.source.y} r="2.5" fill="#64748b" />
                          <circle cx={connection.target.x} cy={connection.target.y} r="2.5" fill="#64748b" />
                        </g>
                      );
                    })}
                  </svg>

                  {nodes.map((node) => {
                    const nodeId = Number(node.id);
                    const layout = nodeLayoutById.get(nodeId) || {
                      x: WORKSPACE_PADDING,
                      y: WORKSPACE_PADDING,
                      width: NODE_WIDTH,
                      height: getDefaultNodeHeight((mappingsByNode.get(nodeId) || []).length),
                    };
                    const nodeMappings = mappingsByNode.get(nodeId) || [];
                    const showAllKeywords = Boolean(expandedNodeKeywords[nodeId]);
                    const visibleKeywords = showAllKeywords ? nodeMappings : nodeMappings.slice(0, 4);

                    return (
                      <Box
                        key={`node-${node.id}`}
                        data-cluster-node-id={String(nodeId)}
                        sx={{
                          position: 'absolute',
                          width: NODE_WIDTH,
                          minHeight: getDefaultNodeHeight(nodeMappings.length),
                          left: layout.x,
                          top: layout.y,
                          zIndex: 2,
                          borderRadius: 2,
                          border: '1px solid rgba(15, 23, 42, 0.2)',
                          background: '#fff',
                          boxShadow: '0 6px 14px rgba(15,23,42,0.10)',
                          cursor: connectSourceId ? 'crosshair' : 'default',
                        }}
                        onClick={() => handleNodeClick(nodeId)}
                      >
                        <Stack
                          data-cluster-node-handle="true"
                          data-node-id={String(nodeId)}
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                          sx={{
                            px: 0.8,
                            py: 0.45,
                            borderTopLeftRadius: 8,
                            borderTopRightRadius: 8,
                            bgcolor: node.color || '#1d4ed8',
                            color: '#fff',
                            cursor: canManage && !connectSourceId ? 'grab' : (connectSourceId ? 'crosshair' : 'default'),
                          }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 700, flexGrow: 1 }} noWrap>
                            {`${node.name} (${nodeMappings.length})`}
                          </Typography>
                          {canManage && (
                            <IconButton
                              data-cluster-node-menu="true"
                              size="small"
                              sx={{ color: '#fff', p: 0.2 }}
                              onPointerDown={(event) => {
                                event.stopPropagation();
                              }}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleMenuOpen(event, node);
                              }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>

                        {nodeMappings.length > 0 && (
                          <Stack spacing={0.45} sx={{ p: 0.7 }}>
                            {visibleKeywords.map((mapping) => (
                              <Chip
                                key={`node-keyword-${node.id}-${mapping.id}`}
                                size="small"
                                variant="outlined"
                                label={mapping.rawKeywordLabel || mapping.normalizedKeyword}
                                sx={{ justifyContent: 'flex-start' }}
                              />
                            ))}

                            {nodeMappings.length > 4 && (
                              <Button
                                size="small"
                                variant="text"
                                sx={{ alignSelf: 'flex-start', px: 0.3, minWidth: 0 }}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setExpandedNodeKeywords((previous) => ({
                                    ...previous,
                                    [nodeId]: !previous[nodeId],
                                  }));
                                }}
                              >
                                {showAllKeywords ? 'Weniger' : `Mehr (${nodeMappings.length - 4})`}
                              </Button>
                            )}
                          </Stack>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {canManage && !!edges.length && (
                <Stack spacing={0.4}>
                  <Typography variant="caption" color="text.secondary">Verbindungen</Typography>
                  {edges.slice(0, 10).map((edge) => (
                    <Stack
                      key={`edge-list-${edge.id}`}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={0.8}
                    >
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {`${nodeById.get(Number(edge.sourceNodeId))?.name || '-'} -> ${nodeById.get(Number(edge.targetNodeId))?.name || '-'}`}
                      </Typography>
                      <Button size="small" variant="text" color="error" onClick={() => onDeleteEdge(edge.id)}>
                        Entfernen
                      </Button>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleRenameNode}>Titel bearbeiten</MenuItem>
        {colorOptions.map((option) => (
          <MenuItem key={`node-color-${option.value}`} onClick={() => handleChangeColor(option.value)}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{ width: 12, height: 12, borderRadius: 99, bgcolor: option.value }} />
              <span>{option.label}</span>
            </Stack>
          </MenuItem>
        ))}
        {!connectSourceId && <MenuItem onClick={() => handleStartConnection(menuNode?.id)}>Verbindung starten</MenuItem>}
        {!!connectSourceId && Number(connectSourceId) !== Number(menuNode?.id) && (
          <MenuItem onClick={handleConnectWithMenuNode}>Mit Quelle verbinden</MenuItem>
        )}
        {connectedEdgeIdsForMenuNode.map((edgeId) => (
          <MenuItem key={`disconnect-edge-${edgeId}`} onClick={() => handleDisconnectEdge(edgeId)}>
            {`Verbindung lösen: ${edgeLabelById.get(edgeId) || 'Verbindung'}`}
          </MenuItem>
        ))}
        <MenuItem onClick={handleDeleteNode} sx={{ color: 'error.main' }}>Löschen</MenuItem>
      </Menu>
    </Stack>
  );
}
